import { Box } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import {
  getInputBackgroundColor,
  getInputBorderColor,
  getInputErrorBackgroundColor,
  getInputErrorShadow,
  getInputTextColor,
  inputErrorBorderColor,
  inputFocusBorderColor,
  inputRadius,
} from "#app/theme/v2/inputStates";
import { spacing } from "#app/theme/v2/spacing";

import { TEXT_LETTER_SPACING, TEXT_LINE_HEIGHTS } from "../Typographies/Text";
import classes from "./InputSuffix.module.css";

type InputSuffixProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  suffix: string;
  disabled?: boolean;
  error?: boolean;
  "aria-label"?: string;
};

const InputSuffix = ({
  value,
  onChange,
  onBlur,
  placeholder,
  suffix,
  disabled,
  error,
  "aria-label": ariaLabel,
}: InputSuffixProps) => {
  const hasValue = Boolean(value);

  return (
    <Box
      className={classes.container}
      data-error={error || undefined}
      style={
        {
          height: spacing[9],
          paddingInline: spacing[4],
          borderRadius: inputRadius,
          "--container-bg": getInputBackgroundColor({ disabled, hasValue }),
          "--container-bd": getInputBorderColor({ disabled, hasValue }),
          "--container-focus-bd": inputFocusBorderColor,
          "--container-error-bd": inputErrorBorderColor,
          "--container-error-bg": getInputErrorBackgroundColor({ hasValue }),
          "--container-error-shadow": getInputErrorShadow({ hasValue }),
        } as React.CSSProperties
      }
    >
      <input
        className={classes.field}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        style={
          {
            "--field-fz": fontSizes[14],
            "--field-fw": fontWeights.regular,
            "--field-lh": TEXT_LINE_HEIGHTS.small,
            "--field-ls": TEXT_LETTER_SPACING.regular.small,
            "--field-color": colors.text.primaryDefault,
            "--field-placeholder-color": colors.text.secondaryDefault,
            "--field-disabled-color": getInputTextColor({ disabled, hasValue }),
            "--field-disabled-placeholder-color": colors.text.secondaryDisabled,
          } as React.CSSProperties
        }
      />
      <span
        className={classes.suffix}
        style={
          {
            "--suffix-fz": fontSizes[14],
            "--suffix-fw": fontWeights.regular,
            "--suffix-lh": TEXT_LINE_HEIGHTS.small,
            "--suffix-ls": TEXT_LETTER_SPACING.regular.small,
            "--suffix-color": getInputTextColor({ disabled, hasValue }),
            "--suffix-gap": spacing[1],
          } as React.CSSProperties
        }
      >
        {suffix}
      </span>
    </Box>
  );
};

export { InputSuffix };
